/**
 * Settings → Licence (Enterprise): who this AIOE is licensed to, what it allows, when it
 * expires, and how to install a new token (docs/plans/2026-10-update-management.md).
 */

import type { LicenceView } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { Button } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { timeAgo, useLoad } from '../hooks.ts';

const day = (iso: string) => dateLong(iso);

export function stateMark(v: LicenceView) {
  switch (v.state) {
    case 'licensed':
      return <StatusMark state="online" label="Licensed" />;
    case 'expiring':
      return <StatusMark state="medium" label="Expiring soon" />;
    case 'grace':
      return <StatusMark state="high" label="Expired, in grace" />;
    case 'lapsed':
      return <StatusMark state="error" label="Lapsed" />;
    case 'trial':
      return <StatusMark state="idle" label="Unlicensed trial" />;
    case 'trial-ended':
      return <StatusMark state="error" label="Trial ended" />;
    default:
      return <StatusMark state="online" label="Mojo Up AI Cloud" />;
  }
}

export function LicencePanel() {
  const licence = useLoad(() => routes.licence(), []);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const act = async (f: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(true);
    try {
      await f();
      licence.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const v = licence.data;
  const plan = v ? (v.plan === 'enterprise' ? 'Enterprise' : v.plan === 'teams' ? 'Teams' : 'Free') : undefined;

  return (
    <ListGroup id="licence">
      {v?.banner ? (
        <ListBlock>
          <p className={`rounded-lg px-3 py-2 ${v.banner.tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}>{v.banner.text}</p>
        </ListBlock>
      ) : null}
      {error || licence.error ? (
        <ListBlock>
          <ErrorNote error={error ?? licence.error} />
        </ListBlock>
      ) : null}
      {v ? (
        <>
          <ListRow title="Status" control={stateMark(v)} />
          <ListRow title="Devices run as" value={plan} />
          {v.holder ? <ListRow title="Licensed to" value={v.holder} /> : null}
          <ListRow
            title="People"
            value={
              <span className="tabular-nums">
                {v.peopleInUse} used a workbench this month{v.seats !== undefined ? ` of ${v.seats === 0 ? 'unlimited' : v.seats} licensed` : ''} · {v.nodesInUse} {v.nodesInUse === 1 ? 'node' : 'nodes'}
              </span>
            }
          />
          {v.expiresAt ? <ListRow title="Expires" value={`${day(v.expiresAt)} (${v.graceDays} days of grace)`} /> : v.type === 'unlimited' ? <ListRow title="Expires" value="Never" /> : null}
          {v.trialEndsAt && (v.state === 'trial' || v.state === 'trial-ended') ? <ListRow title={`Trial ${v.state === 'trial' ? 'ends' : 'ended'}`} value={day(v.trialEndsAt)} /> : null}
          {v.supportedUntil ? <ListRow title="Updates until" value={day(v.supportedUntil)} /> : null}
          <ListRow title="Features" description={v.features.join(', ')} />
          {v.licenceId ? (
            <ListRow
              title="Check-in"
              description={
                <>
                  {v.checkIn === 'off' ? 'Off' : v.lastCheckInAt ? `Last ${timeAgo(v.lastCheckInAt)}` : 'Not yet'}
                  {v.lastCheckInError ? <span className="text-danger"> · {v.lastCheckInError}</span> : null}
                </>
              }
              control={
                v.checkIn !== 'off' ? (
                  <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => act(() => routes.licenceCheckIn())}>
                    Check in now
                  </Button>
                ) : undefined
              }
            />
          ) : null}
        </>
      ) : null}
      <ListRow title={v?.licenceId ? 'Replace the licence' : 'Install a licence'} description="Paste the licence token from the Mojo Up customer portal; it starts with saa1.">
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-start"
          onSubmit={(e) => {
            e.preventDefault();
            void act(async () => {
              await routes.installLicence(token.trim());
              setToken('');
            });
          }}
        >
          <textarea className={`${inputClass} mono min-h-16 flex-1 text-xs`} aria-label="Licence token" placeholder="saa1.…" value={token} onChange={(e) => setToken(e.target.value)} />
          <Button size="sm" type="submit" isDisabled={busy || !token.trim()}>
            {v?.licenceId ? 'Replace licence' : 'Install licence'}
          </Button>
        </form>
      </ListRow>
    </ListGroup>
  );
}
